var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { formatDistance } from "date-fns";
import { isOk } from "healthex-common/result";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { computed, observable, reaction, when } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { NavigationHelper } from "./NavigationHelper";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { willNeverHappen } from "healthex-common/utils";
const _pageToRoutes = {
    HEALTHCARE_ORG_ANALYTICS: NamedRoutes.HEALTHCARE_ORG_ANALYTICS,
    PROJECTS: NamedRoutes.PROJECTS,
    PATIENTS: NamedRoutes.PATIENTS,
    PATIENT_SHARING: NamedRoutes.PATIENT_SHARING,
    PATIENT_HOME: NamedRoutes.PATIENT_HOME,
    PATIENT_FTU: NamedRoutes.PATIENT_HOME_ONBOARDING,
    ACCOUNT_SETTINGS: NamedRoutes.ACCOUNT_SETTINGS,
    ADMIN: NamedRoutes.ADMIN,
    GLOBAL_ADMIN: NamedRoutes.GLOBAL_ADMIN,
    RESEARCH_STUDIES: NamedRoutes.RESEARCH_STUDIES,
    WELCOME_ORG_USER_DETAILS: NamedRoutes.WELCOME_ORG_USER_DETAILS,
    WELCOME_BASIC_ORG_DETAILS: NamedRoutes.WELCOME_BASIC_ORG_DETAILS,
    WELCOME_BACK_ORG_USER: NamedRoutes.WELCOME_BACK_ORG_USER,
};
const pageToRoutes = _pageToRoutes;
const WELCOME_ROUTES = [
    NamedRoutes.WELCOME.routeName,
    NamedRoutes.WELCOME_ORG.routeName,
    NamedRoutes.WELCOME_ORG_USER_DETAILS.routeName,
    NamedRoutes.WELCOME_BASIC_ORG_DETAILS.routeName,
    NamedRoutes.WELCOME_CUSTOMIZE_ORG_DETAILS.routeName,
    NamedRoutes.WELCOME_ORG_USER_INVITE.routeName,
    NamedRoutes.WELCOME_BACK_ORG_USER.routeName,
];
export class AppUIManager {
    constructor(authManager, routingUIManager, routingAuthUIManager, organizationsManager, environment) {
        this.authManager = authManager;
        this.routingUIManager = routingUIManager;
        this.routingAuthUIManager = routingAuthUIManager;
        this.organizationsManager = organizationsManager;
        this.environment = environment;
        this.navigationHelper = new NavigationHelper(this.routingUIManager, pageToRoutes);
        this.ftuEducationDialog = new SimpleDialogModel();
        this.visitedCurrentPageDateInterval = null;
        this.urlSearchParams = null;
        reaction(() => routingUIManager.currentRoute, async () => {
            if (!routingUIManager.currentRoute)
                return;
            this.setupCurrentPageSinceTimer();
            if (!this.authManager.isLoggedIn) {
                await this.loginUsingSSOTokenIfPresent();
                if ((this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION ||
                    this.authManager.userType === UserType.EXTERNAL_RESEARCHER) &&
                    this.authManager.isUserProfileEmpty) {
                    this.navigationHelper.redirectTo("WELCOME_ORG_USER_DETAILS");
                }
            }
            else {
                if (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION ||
                    this.authManager.userType === UserType.EXTERNAL_RESEARCHER) {
                    if (!WELCOME_ROUTES.includes(routingUIManager.currentRoute.routeName)) {
                        await when(() => this.organizationSetupComplete !== null);
                        if (this.authManager.isUserProfileEmpty || (this.authManager.isOrgAdmin && !this.organizationSetupComplete)) {
                            this.navigationHelper.redirectTo("WELCOME_BACK_ORG_USER");
                            return;
                        }
                    }
                    if ((routingUIManager.currentRoute.routeName === NamedRoutes.HEALTHCARE_ORG_ANALYTICS.routeName &&
                        this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION) ||
                        (routingUIManager.currentRoute.routeName === NamedRoutes.PROJECTS.routeName &&
                            this.authManager.userType === UserType.EXTERNAL_RESEARCHER)) {
                        await when(() => this.userOnboardingComplete !== null);
                        if (!this.userOnboardingComplete) {
                            this.ftuEducationDialog.show();
                        }
                    }
                }
            }
            if (!this.routingAuthUIManager.isRouteAuthorized(routingUIManager.currentRoute) && this.homepage) {
                this.navigationHelper.redirectTo(this.homepage);
            }
        });
        const indexRedirectIfLoggedIn = () => {
            if (this.homepage !== null) {
                return pageToRoutes[this.homepage];
            }
            return null;
        };
        routingUIManager.registerRedirect(NamedRoutes.INDEX, indexRedirectIfLoggedIn);
    }
    get sinceVisitedCurrentPageTimestamp() {
        if (!this.visitedCurrentPageDate)
            return null;
        const _ = this.forceUpdateSinceVisitedCurrentPageDate;
        return formatDistance(this.visitedCurrentPageDate, new Date(), { addSuffix: true });
    }
    get urlFTUParam() {
        var _a, _b;
        const ftu = (_b = (_a = this.urlSearchParams) === null || _a === void 0 ? void 0 : _a.get("ftu")) !== null && _b !== void 0 ? _b : null;
        if (ftu === null)
            return null;
        return ftu;
    }
    get ignoreFTU() {
        var _a;
        return ((_a = this.urlFTUParam) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === "false";
    }
    get userOnboardingComplete() {
        if (this.organizationsManager.myUser.value === NOT_LOADED || this.organizationsManager.myUser.value === null) {
            return null;
        }
        return this.organizationsManager.myUser.value.onboardingComplete;
    }
    get organizationSetupComplete() {
        if (this.organizationsManager.myOrganization.value === NOT_LOADED || this.organizationsManager.myOrganization.value === null) {
            return null;
        }
        return this.organizationsManager.myOrganization.value.setupComplete;
    }
    get currentPage() {
        const currentPage = this.navigationHelper.currentNavigationItem;
        if (this.authManager.isResearcher && currentPage === "RESEARCH_STUDIES") {
            return "PROJECTS";
        }
        else {
            return currentPage;
        }
    }
    get isDebugMode() {
        return this.environment.CSS_DEBUG;
    }
    setupCurrentPageSinceTimer() {
        this.visitedCurrentPageDate = new Date();
        if (this.visitedCurrentPageDateInterval !== null) {
            window.clearInterval(this.visitedCurrentPageDateInterval);
        }
        this.visitedCurrentPageDateInterval = window.setInterval(() => this.handleForceUpdateSinceVisitedCurrentPageDate(), 60 * 1000);
    }
    handleForceUpdateSinceVisitedCurrentPageDate() {
        this.forceUpdateSinceVisitedCurrentPageDate = new Date();
    }
    get homepage() {
        if (this.authManager.userType === null)
            return null;
        if (this.authManager.userType === UserType.PATIENT ||
            this.authManager.userType === UserType.SCREENSHOT_USER ||
            this.authManager.userType === UserType.PATIENT_READ_ONLY) {
            return "PATIENT_HOME";
        }
        else if (this.authManager.userType === UserType.EXTERNAL_RESEARCHER) {
            return "PROJECTS";
        }
        else if (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION) {
            if (this.authManager.permissions.includes(UserPermissions.ORG_ADMIN) ||
                this.authManager.permissions.includes(UserPermissions.RESEARCH_ADMIN)) {
                return "HEALTHCARE_ORG_ANALYTICS";
            }
            else {
                return "PROJECTS";
            }
        }
        else if (this.authManager.userType === UserType.SERVICE_PROVIDER) {
            return "HEALTHCARE_ORG_ANALYTICS";
        }
        else {
            return willNeverHappen(this.authManager.userType);
        }
    }
    navigateToPage(page) {
        this.navigationHelper.navigateTo(page);
    }
    navigateToHome() {
        if (this.homepage === null)
            return;
        this.navigationHelper.navigateTo(this.homepage);
    }
    async logout() {
        const userType = this.authManager.userType;
        if (!userType)
            return;
        this.authManager.logout();
    }
    async loginUsingSSOTokenIfPresent() {
        const params = new URLSearchParams(window.location.href.split("?")[1]);
        const token = params.get("ssoToken");
        if (!token) {
            return false;
        }
        const result = await this.authManager.handleSAMLLogin(token);
        if (isOk(result)) {
            window.location.search = new URLSearchParams().toString();
            return true;
        }
        return false;
    }
    updateSearchParams(locationSearch) {
        this.urlSearchParams = new URLSearchParams(locationSearch);
    }
}
__decorate([
    observable
], AppUIManager.prototype, "visitedCurrentPageDate", void 0);
__decorate([
    observable
], AppUIManager.prototype, "forceUpdateSinceVisitedCurrentPageDate", void 0);
__decorate([
    computed
], AppUIManager.prototype, "sinceVisitedCurrentPageTimestamp", null);
__decorate([
    observable
], AppUIManager.prototype, "urlSearchParams", void 0);
__decorate([
    computed
], AppUIManager.prototype, "urlFTUParam", null);
__decorate([
    computed
], AppUIManager.prototype, "ignoreFTU", null);
__decorate([
    computed
], AppUIManager.prototype, "userOnboardingComplete", null);
__decorate([
    computed
], AppUIManager.prototype, "organizationSetupComplete", null);
__decorate([
    computed
], AppUIManager.prototype, "currentPage", null);
__decorate([
    computed
], AppUIManager.prototype, "homepage", null);
